// Converted MUI-based version of FinBot AI Chat UI with sidebar layout

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  Switch,
  Drawer,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';

const quickActions = [
  '📊 View Portfolio',
  '🔍 Find Cheap Stocks',
  '📈 High ROE Stocks',
  '🧪 Backtest Strategy',
];

const ChatMessage = ({ sender, content }) => (
  <Box
    alignSelf={sender === 'user' ? 'flex-end' : 'flex-start'}
    bgcolor={sender === 'user' ? 'primary.main' : 'grey.200'}
    color={sender === 'user' ? 'white' : 'text.primary'}
    px={2}
    py={1}
    borderRadius={2}
    maxWidth="75%"
    my={0.5}
  >
    <Typography variant="body2" fontSize={13}>{content}</Typography>
  </Box>
);

function StrategyChatBot() {
  const [messages, setMessages] = useState([
    {
      sender: 'bot',
      content: '👋 Welcome to FinBot! Ask me about stocks, trading, or strategies.',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const sendMessage = async (msg) => {
    const message = msg || input.trim();
    if (!message) return;

    setMessages((prev) => [...prev, { sender: 'user', content: message }]);
    setInput('');
    setLoading(true);

    try {
      const response = await new Promise((resolve) =>
        setTimeout(() =>
          resolve({
            data: {
              message: `Echo: ${message}`,
              agent_type: 'nl_query',
            },
          }), 1000)
      );

      setMessages((prev) => [
        ...prev,
        { sender: 'bot', content: response.data.message },
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { sender: 'bot', content: '❌ Error processing request.' },
      ]);
    }

    setLoading(false);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return (
    <Box  sx={{ display: 'flex', height: '75vh', borderRadius: 2, overflow: 'hidden' }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: 220,
          bgcolor: 'background.paper',
          borderRight: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          height: '75vh',
          overflowY: 'auto'
        }}
      >
        <Box bgcolor="primary.main" color="white" p={2}>
          <Typography variant="subtitle1">🤖 AlgoFabric Finance Bot</Typography>
          <Chip
            label="Live Trading"
            size="small"
            sx={{ mt: 1, background: 'rgba(255,255,255,0.2)', color: '#fff' }}
          />
        </Box>

        <Box p={1}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            QUICK ACTIONS
          </Typography>
          <List dense>
            {["📊 View Portfolio", "🔍 Find Cheap Stocks", "📈 High ROE Stocks", "🧪 Backtest Strategy"].map((action, i) => (
              <ListItem key={i} disablePadding>
                <Button
                  fullWidth
                  sx={{ justifyContent: 'flex-start', color: '#2d4890', textTransform: 'none', py: 1.5 }}
                  onClick={() => alert(`You clicked: ${action}`)}
                >
                  {action}
                </Button>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box p={2}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            TRADING CONTROLS
          </Typography>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fb', borderRadius: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Enable Trading</Typography>
              <Switch defaultChecked size="small" />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Auto-confirm Orders</Typography>
              <Switch defaultChecked size="small" />
            </Box>
            <Typography variant="caption" color="warning.main" mt={1} display="block">
              ⚠️ Trading is enabled. All orders require confirmation.
            </Typography>
          </Paper>
        </Box>

        <Box p={2}>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            EXAMPLE QUERIES
          </Typography>
          <Typography variant="body2">"Find banking stocks with PE &lt; 15"</Typography>
        </Box>
      </Box>

      {/* Main Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" color="default" elevation={2} sx={{ background: 'linear-gradient(90deg, #5B9DF9, #2563eb)', color: 'white' }}>
          <Toolbar sx={{ minHeight: 44 }}>
            <ChatIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>🤖 AI Trading Assistant</Typography>
          </Toolbar>
        </AppBar>

        <Box flex={1} p={2} overflow="auto" display="flex" flexDirection="column">
          {messages.map((msg, index) => (
            <ChatMessage key={index} sender={msg.sender} content={msg.content} />
          ))}
          {loading && (
            <Box alignSelf="flex-start" my={1}>
              <CircularProgress size={20} sx={{ color: 'primary.main' }} />
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        <Paper
          elevation={3}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            sendMessage();
          }}
          sx={{ p: 1.5, display: 'flex', borderTop: '1px solid #e0e0e0' }}
        >
          <TextField
            multiline
            minRows={1}
            maxRows={6}
            sx={{ flex: 1, maxWidth: '75%', resize: 'none' }}

            variant="outlined"
            placeholder="Ask about backtesting..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            size="small"
            inputProps={{ style: { fontSize: 13 } }}
          />
          <IconButton type="submit" color="primary" sx={{ ml: 1 }} disabled={loading}>
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Paper>
      </Box>
    </Box>
  );
}

export default StrategyChatBot;
